import React, { useState, useEffect, useRef } from 'react';
import { Navbar, Nav, Container, Form, FormControl, Button } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { FiMapPin, FiPhone } from "react-icons/fi";
import { FaRegEnvelope } from "react-icons/fa6";
import CatalogNavItem from './CatalogNavItem';
import LoadingSpinner from '../components/LoadingSpinner';
import logo from '../images/logo.png';
import './Header.css';

import productService from '../services/productService';
import { showNotification } from '../store/actions';

const Header = () => {
    const [visible, setVisible] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const [expanded, setExpanded] = useState(false);
    // const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [noResultsFound, setNoResultsFound] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const searchRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const location = useLocation();

    const topbarHeight = 50; // Высота topbar для расчетов

    
    // Эффект для извлечения поискового запроса из URL при загрузке страницы
    useEffect(() => {
        // Создаем объект URLSearchParams для парсинга query-параметров
        const queryParams = new URLSearchParams(location.search);
        const searchParamFromUrl = queryParams.get('search');
        
        // Если в URL есть параметр search, устанавливаем его в поле поиска
        if (searchParamFromUrl) {
            setSearchQuery(searchParamFromUrl);
            
            // Загружаем результаты в состояние, но НЕ показываем их
            // (асинхронно предзагружаем для быстрого отображения при фокусе)
            (async () => {
                const result = await productService.quickSearchProducts(searchParamFromUrl);
                if (result.success) {
                    setSearchResults(result.data);
                    setNoResultsFound(result.data.length === 0);
                    // НЕ устанавливаем setShowResults(true)
                }
            })();
        }
    }, [location.pathname, location.search]);
    

    // Функция для отслеживания прокрутки
    const handleScroll = () => {
        // Применяем логику прокрутки, если не в мобильном режиме
        if (!isMobile) {
            if (window.scrollY > topbarHeight) {
                setVisible(false);
            } else {
                setVisible(true);
            }
        }
    };
    
    // Функция для отслеживания размера окна
    const handleResize = () => {
        const isMobileView = window.innerWidth < 992;
        
        if (isMobileView) {
            setIsMobile(true);
            setVisible(false);
        } else {
            setIsMobile(false);
            // Восстанавливаем видимость, если страница не прокручена
            if (window.scrollY <= topbarHeight) {
                setVisible(true);
            }
        }
    };

    // Effect для определения прокрутки и изменения размера окна
    useEffect(() => {
        // Проверяем начальный размер окна
        handleResize();

        // После первоначальной настройки устанавливаем флаг загрузки в false
        setTimeout(() => {
            setIsInitialLoad(false);
        }, 50);
        
        // Добавляем обработчики событий
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);
        
        // Очистка обработчиков при размонтировании компонента
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [isMobile]);

    const fetchSearchResults = async (query) => {
        if (query && query.length >= 3) {
            // setIsSearchLoading(true);
            setNoResultsFound(false);
            
            const result = await productService.quickSearchProducts(query)
            
            // setIsSearchLoading(false);

            if (result.success) {
                setSearchResults(result.data);
                setShowResults(true);
                setNoResultsFound(result.data.length === 0);
            } else {
                setSearchResults([]);
                setShowResults(true);
                setNoResultsFound(true);
                dispatch(showNotification(result.error, 'error'));
            }
        } else {
            setSearchResults([]);
            setShowResults(false);
            setNoResultsFound(false);
        }
    };

    // Обработчик изменения поля ввода с debounce
    const handleInputChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        
        // Debounce для предотвращения слишком частых запросов
        // Создаем локальную переменную для хранения таймера
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            fetchSearchResults(query);
        }, 300);
    };

    useEffect(() => {
        // Очистка таймера при размонтировании
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Effect для автоматического фокуса на инпут при открытии поиска в мобильной версии
    useEffect(() => {
        if (showMobileSearch && searchRef.current) {
            searchRef.current.focus();
        }
    }, [showMobileSearch]);

    // Функция для обработки клика вне поисковой формы
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target) && showMobileSearch) {
                setShowMobileSearch(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMobileSearch]); 

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/catalog?search=${encodeURIComponent(searchQuery)}`);
            setShowResults(false);
            setShowMobileSearch(false);
        }
    };

    const handleResultClick = (result) => {
        navigate(`/catalog/${result.category}/${result.name}-${result.id}`);
        setShowResults(false);
        setShowMobileSearch(false);
    };

    const handleSearchInputBlur = () => {
        // Не закрываем результаты сразу, чтобы успеть кликнуть на них
        setTimeout(() => setShowResults(false), 200);
    };

    const toggleMobileSearch = () => {
        setShowMobileSearch(!showMobileSearch);
        if (!showMobileSearch) {
            // setSearchQuery('');
        }
    };

    // Закрытие меню (гамбургера) при изменении маршрута
    useEffect(()=>{
        setExpanded(false);
    }, [navigate]);
    
    // Функция для рендеринга содержимого результатов поиска
    const renderSearchResults = () => {
        // if (isSearchLoading) {
        //     return (
        //         <div className="p-3 text-center">
        //             <LoadingSpinner size="sm" />
        //         </div>
        //     );
        // }
        
        if (noResultsFound) {
            return (
                <div className="p-3 text-center text-secondary">
                    Ничего не найдено
                </div>
            );
        }

        const maxArticlesToShow = 2;

        return searchResults.map((result) => {
            const baseArticles = result.matchedByArticle
                ? result.matchedArticles
                : result.allArticles;

            const articlesToShow = baseArticles?.slice(0, maxArticlesToShow) || [];
            const extraCount = baseArticles?.length > maxArticlesToShow
                ? baseArticles.length - maxArticlesToShow
                : 0;

            return (
                <div
                    key={result.id}
                    className="p-2 search-result-item"
                    onClick={() => handleResultClick(result)}
                >
                    {result.title}

                    {articlesToShow.length > 0 && (
                        <div className="small text-secondary">
                            Артикул: {articlesToShow.join(', ')}
                            {extraCount > 0 && ` + ещё ${extraCount}`}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <header>
            {/* Верхняя панель с контактной информацией */}
            <div 
                className="topbar bg-light py-2 d-none d-lg-flex align-items-center" 
                style={{ 
                    position: 'fixed',
                    top: 0,
                    width: '100%', 
                    minHeight: `${topbarHeight}px`,
                    zIndex: 1030,
                    transform: visible ? 'translateY(0)' : 'translateY(-100%)',
                    transition: isInitialLoad ? 'none' : 'transform 0.38s ease-in-out'
                }}
            >
                <Container className="d-flex justify-content-center align-items-center">
                    <div className="contact-info d-flex align-items-center gap-4">
                        <a 
                            href="https://yandex.ru/maps/?text=г.+Тюмень,+ул.+Мельничная,+д.+17" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="me-4 d-flex align-items-center text-secondary text-decoration-none"
                        >
                            <FiMapPin className="me-2" size={22} />
                            <span>г. Тюмень, ул. Мельничная 17</span>
                        </a>
                        <a 
                            href="tel:+73452614940" 
                            className="me-4 d-flex align-items-center text-secondary text-decoration-none"
                        >
                            <FiPhone className="me-2" size={22} />
                            <span>+7 (3452) 61-49-40</span>
                        </a>
                        <a 
                            href="mailto:dos.sreda@yandex.ru" 
                            className="d-flex align-items-center text-secondary text-decoration-none"
                        >
                            <FaRegEnvelope className="me-2" size={22} />
                            <span>dos.sreda@yandex.ru</span>
                        </a>
                    </div>
                </Container>
            </div>

            {/* Основная навигационная панель */}
            <Navbar 
                bg="white" 
                expand="lg" 
                expanded={expanded}
                onToggle={(expanded) => setExpanded(expanded)}
                className="shadow-sm py-3"
                style={{ 
                    position: 'fixed',
                    top: visible ? `${topbarHeight}px` : '0',
                    width: '100%',
                    minHeight: '80px',
                    zIndex: 1029,
                    transition: isInitialLoad ? 'none' : 'top 0.3s ease-in-out'
                }}
            >
                <Container>
                    {/* Мобильный вид: Бургер-меню слева, логотип прижат влево, лупа прижата вправо */}
                    <div className="d-flex d-lg-none align-items-center w-100">
                        <Navbar.Toggle aria-controls="basic-navbar-nav" className="border-0 p-0 me-3" />
                        
                        {/* Логотип для md и выше (всегда показываем) */}
                        <Navbar.Brand 
                            as={Link} 
                            to="/" 
                            className="p-0 m-0 d-none d-md-block"
                        >
                            <img 
                                src={logo} 
                                height="48" 
                                className="d-inline-block align-top" 
                                alt="Перформанс лого" 
                            />
                        </Navbar.Brand>

                        {/* Логотип для мобильных (скрывается, если showMobileSearch === true) */}
                        {!showMobileSearch && (
                            <Navbar.Brand 
                                as={Link} 
                                to="/" 
                                className="p-0 m-0 d-md-none"
                            >
                                <img 
                                    src={logo} 
                                    height="40" 
                                    className="d-inline-block align-top" 
                                    alt="Перформанс лого" 
                                />
                            </Navbar.Brand>
                        )}
                        
                        <div className="ms-auto">
                            {/* xs: Мобильная строка ввода */}
                            <div className="d-flex d-md-none align-items-center mobile-search-container" ref={searchRef}>
                                {showMobileSearch ? (
                                    <Form onSubmit={handleSearchSubmit} className="d-flex mobile-search-expanded">
                                        <div className="position-relative w-100">
                                            <Form.Control
                                                type="text"
                                                placeholder="Поиск по каталогу..."
                                                value={searchQuery}
                                                onChange={handleInputChange}
                                                onFocus={() => setShowResults(true)}
                                                className="rounded-pill py-2"
                                                style={{ width: '100%' }}
                                            />
                                            {searchQuery && (
                                                <Button 
                                                    variant="link" 
                                                    onClick={() => {
                                                        setSearchQuery('');
                                                        setSearchResults([]);
                                                        setShowResults(false);
                                                        setNoResultsFound(false);
                                                    }}
                                                    className="position-absolute end-0 top-0 h-100 px-3 text-secondary border-0"
                                                    style={{ marginRight: '30px' }}
                                                >
                                                    <FaTimes size={20} />
                                                </Button>
                                            )}
                                            <Button 
                                                variant="link" 
                                                type={searchQuery.trim() ? "submit" : "button"}
                                                onClick={searchQuery.trim() ? null : toggleMobileSearch}
                                                className="position-absolute end-0 top-0 h-100 px-3 text-secondary border-0"
                                            >
                                                <FaSearch size={20} />
                                            </Button>
                                        </div>
                                    </Form>
                                ) : (
                                    <Button 
                                        variant="link" 
                                        onClick={toggleMobileSearch}
                                        className="border-0 text-secondary p-2"
                                    >
                                        <FaSearch size={24} />
                                    </Button>
                                )}
                                
                                {/* Выпадающие результаты поиска для мобильной версии */}
                                {showResults && showMobileSearch && (
                                    <div className="search-results position-absolute bg-white shadow-sm rounded mt-1 z-1" 
                                        style={{ top: '65px', left: '0', width: '100%' }}>
                                        <div className="search-results-container">
                                            {renderSearchResults()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* sm и выше: Поисковая форма с живым поиском */}
                            <div className="d-none d-md-block position-relative">
                                <Form onSubmit={handleSearchSubmit} className="d-flex">
                                    <div className="position-relative w-100">
                                        <Form.Control
                                            type="text"
                                            placeholder="Поиск по каталогу..."
                                            value={searchQuery}
                                            onChange={handleInputChange}
                                            onFocus={() => setShowResults(true)}
                                            onBlur={handleSearchInputBlur}
                                            className="rounded-pill pe-5 py-2"
                                        />
                                        {searchQuery && (
                                            <Button 
                                                variant="link" 
                                                onClick={() => {
                                                    setSearchQuery('');
                                                    setSearchResults([]);
                                                    setShowResults(false);
                                                    setNoResultsFound(false);
                                                }}
                                                className="position-absolute end-0 top-0 h-100 px-3 text-secondary border-0"
                                                style={{ marginRight: '30px' }}
                                            >
                                                <FaTimes size={20} />
                                            </Button>
                                        )}
                                        <Button 
                                            variant="link" 
                                            type={searchQuery.trim() ? "submit" : "button"}
                                            className="position-absolute end-0 top-0 h-100 px-3 text-secondary border-0"
                                        >
                                            <FaSearch size={20} />
                                        </Button>
                                    </div>
                                </Form>
                                
                                {/* Выпадающие результаты поиска */}
                                {showResults && (
                                    <div className="search-results position-absolute w-100 bg-white shadow-sm rounded mt-1 z-1">
                                        <div className="search-results-container">
                                            {renderSearchResults()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Десктопный вид: Логотип, меню, поисковая кнопка */}
                    <Navbar.Brand as={Link} to="/" className="d-none d-lg-block p-0 m-0">
                        <img 
                            src={logo} 
                            height="48" 
                            className="d-inline-block align-top" 
                            alt="Перформанс лого" 
                        />
                    </Navbar.Brand>
                    
                    <Navbar.Collapse id="basic-navbar-nav">
                        <Nav className="mx-auto gap-3 pt-3 pt-lg-0">
                            <Nav.Link as={Link} to="/">Главная</Nav.Link>
                            <Nav.Link as={Link} to="/catalog" className="d-flex d-lg-none">Каталог</Nav.Link>
                            {/* Использование выделенного компонента для пункта "Каталог" и его выпадающего меню */}
                            <CatalogNavItem />
                            <Nav.Link as={Link} to="/about">О компании</Nav.Link>
                            <Nav.Link as={Link} to="/contacts">Контакты</Nav.Link>
                        </Nav>
                    </Navbar.Collapse>
                    
                    {/* Десктопный вид: Поисковая форма с живым поиском */}
                    <div className="d-none d-lg-block position-relative">
                        <Form onSubmit={handleSearchSubmit} className="d-flex">
                            <div className="position-relative w-100">
                                <Form.Control
                                    type="text"
                                    placeholder="Поиск по каталогу..."
                                    value={searchQuery}
                                    onChange={handleInputChange}
                                    onFocus={() => setShowResults(true)}
                                    onBlur={handleSearchInputBlur}
                                    className="rounded-pill pe-5 py-2"
                                />
                                {searchQuery && (
                                    <Button 
                                        variant="link" 
                                        onClick={() => {
                                            setSearchQuery('');
                                            setSearchResults([]);
                                            setShowResults(false);
                                            setNoResultsFound(false);
                                        }}
                                        className="position-absolute end-0 top-0 h-100 px-3 text-secondary border-0"
                                        style={{ marginRight: '30px' }}
                                    >
                                        <FaTimes size={20} />
                                    </Button>
                                )}
                                <Button 
                                    variant="link" 
                                    type={searchQuery.trim() ? "submit" : "button"}
                                    className="position-absolute end-0 top-0 h-100 px-3 text-secondary border-0"
                                >
                                    <FaSearch size={20} />
                                </Button>
                            </div>
                        </Form>
                        
                        {/* Выпадающие результаты поиска */}
                        {showResults && (
                            <div className="search-results position-absolute w-100 bg-white shadow-sm rounded mt-1 z-3">
                                <div className="search-results-container">
                                    {renderSearchResults()}
                                </div>
                            </div>
                        )}
                    </div>
                </Container>
            </Navbar>

            {/* Пустое пространство для компенсации фиксированных панелей */}
            <div style={{ 
                height: visible ? `${topbarHeight + 80}px` : '80px',
                transition: isInitialLoad ? 'none' : 'height 0.3s ease-in-out'
            }}></div>
        </header>
    );
}

export default Header;